import { 
  Box, 
  Container, 
  Grid, 
  Link, 
  Typography, 
  Divider,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logo from '../../assets/images/logo.png';
import WaLogo from '../../assets/images/Wa 2.jpg'

interface FooterLink {
  label: string;
  href?: string;
  route?: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: "Quick Links",
    links: [
      { label: "Home", href: "/" },
      { label: "Sign Up", href: "/sign-up" },
      { label: "Pricing", href: "/pricing" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms & Conditions", route: "/terms" },
      { label: "Privacy Policy", route: "/privacy" },
    ],
  },
];

export default function Footer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.background.paper 
          : '#f5f5f5',
        borderTop: `2px solid ${theme.palette.primary.main}`,
        mt: 8,
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Logo & Contact Section */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ mb: 3 }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  width: '200px', 
                  maxWidth: '100%',
                  height: 'auto',
                  marginBottom: '1rem'
                }} 
              />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.primary.main }}>
              Contact Us
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit' }}>
                📍 Address
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                No 1/50, P.K Street Mettu Kantigai, Gudapakkam, Chennai, Thiruvallur, Tamil Nadu, Pin: 600124
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit' }}>
                📞 Phone
              </Typography>
              <Link href="tel:+917845381202" sx={{ textDecoration: 'none' }}>
                <Typography variant="caption" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  +91 7845381202
                </Typography>
              </Link>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit' }}>
                🕐 Hours
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mon - Sun / 9:00 AM - 7:00 PM
              </Typography>
            </Box>
          </Grid>

          {/* Links Sections */}
          {footerSections.map((section, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.links.map((link, linkIdx) => {
                  const isInternalRoute = 'route' in link && link.route;
                  return isInternalRoute ? (
                    <Typography
                      key={linkIdx}
                      component="button"
                      variant="body2"
                      onClick={() => navigate(link.route!)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        textDecoration: 'none',
                        color: theme.palette.mode === 'dark' 
                          ? theme.palette.grey[400] 
                          : 'text.secondary',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        p: 0,
                        '&:hover': {
                          color: 'primary.main',
                          pl: 1,
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  ) : (
                    <Link
                      key={linkIdx}
                      href={link.href}
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        textDecoration: 'none',
                        color: theme.palette.mode === 'dark' 
                          ? theme.palette.grey[400] 
                          : 'text.secondary',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: 'primary.main',
                          pl: 1,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </Box>
            </Grid>
          ))}

          {/* WhatsApp Section */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
              Connect With Us
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
            }}>
              <img 
                src={WaLogo} 
                alt="WhatsApp" 
                style={{ 
                  maxWidth: '160px', 
                  height: 'auto',
                  borderRadius: '8px',
                }} 
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Chat with us on WhatsApp
            </Typography>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ 
          my: 3,
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.1)'
        }} />

        {/* Bottom Copyright Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {'Copyright © '}
            <Link 
              href="https://satztechsolutions.com/" 
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              SatzTech Solutions
            </Link>
            {` ${currentYear}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            All rights reserved. Matrimony made simple.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
